import dayjs from '@/helpers/dayjs';
import { yupAddress, yupBoolean, yupMixed, yupNumber, yupObject, yupPeriodicValue, yupString } from '../../yup';
import type { DepositTypeEnum, IncomingProperty, PropertyTypeEnum, StampDutyStatusEnum } from '@/types';
import { DepositType, PropertyPurpose, StampDutyStatus, standardBridgeTypes } from '@/helpers/const';
import { useFeatureFlags, FeatureFlag } from '@/helpers/useFeatureFlags';

const isNegativeGearingFy27Enabled = () => useFeatureFlags().isEnabled(FeatureFlag.NEGATIVE_GEARING_FY27);

const isNewBuildQuestionApplicable = (propertyType: PropertyTypeEnum, propertyPurpose?: string) =>
  isNegativeGearingFy27Enabled() &&
  standardBridgeTypes.includes(propertyType as 'residential' | 'rural_residential' | 'off_plan_residential') &&
  propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY;

export const incomingPropertySchemaV1 = yupObject<IncomingProperty>({
  propertyType: yupMixed<PropertyTypeEnum>()
    .notOneOf(['vacant_land'], 'We currently do not accept vacant land as security')
    .required()
    .label('Property type'),
  address: yupAddress().required().label('Property address'),
  propertyPurpose: yupString().required().label('Property purpose'),
  contractForSale: yupBoolean().required().label('Contract for sale response'),
  isNewBuild: yupBoolean()
    .when(['propertyType', 'propertyPurpose'], ([propertyType, propertyPurpose], schema) =>
      isNewBuildQuestionApplicable(propertyType, propertyPurpose) ? schema.required() : schema,
    )
    .label('New build response'),
  isContractExchanged: yupBoolean()
    .when(
      ['propertyType', 'propertyPurpose', 'isNewBuild', 'contractForSale'],
      ([propertyType, propertyPurpose, isNewBuild, contractForSale], schema) =>
        isNewBuildQuestionApplicable(propertyType, propertyPurpose) && isNewBuild === false && contractForSale === true
          ? schema.required()
          : schema,
    )
    .label('Contract exchange response'),
  settlementDate: yupString()
    .required()
    .isoDate()
    .test('validDate', '${path} must be within 12 months', (value) => {
      const date = dayjs(value);
      return date.isAfter(dayjs(), 'day') && date.isBefore(dayjs().add(1, 'year'), 'day');
    })
    .label('Purchase date'),
  purchaseAmount: yupNumber().required().label('Purchase price'),
  receiveRentalIncome: yupBoolean().required().label('Rental income response'),
  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('receiveRentalIncome', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Expected rental income'),
  depositAmount: yupNumber()
    .when('purchaseAmount', ([purchaseAmount], schema) =>
      schema.max(purchaseAmount, `The deposit cannot be more than the purchase price`),
    )
    .required()
    .label('Deposit amount'),
  depositType: yupMixed<DepositTypeEnum>().oneOf(Object.values(DepositType)).required().label('Deposit payment method'),
  stampDutyStatus: yupMixed<StampDutyStatusEnum>()
    .oneOf(Object.values(StampDutyStatus))
    // already_paid is only valid if contract for sale exists
    .when('contractForSale', {
      is: true,
      then: (schema) => schema.oneOf(Object.values(StampDutyStatus)),
      otherwise: (schema) =>
        schema.oneOf(Object.values(StampDutyStatus).filter((item) => item !== StampDutyStatus.ALREADY_PAID)),
    })
    .required()
    .label('Stamp duty response'),
});
